import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Timestamp } from "firebase/firestore";

const CareProfileForm = ({
  profileToEdit,
  onFormSubmit,
  onCancel,
  isInitialProfile = false,
  initialProfileName = "",
}) => {
  const { addCareProfile, updateCareProfile } = useAuth();
  const [profileName, setProfileName] = useState(initialProfileName || "");
  const [dob, setDob] = useState("");
  const [primaryCaregiver, setPrimaryCaregiver] = useState("");
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (profileToEdit) {
      setProfileName(profileToEdit.profileName || "");
      try {
        setDob(
          profileToEdit.dob
            ? new Date(profileToEdit.dob.seconds * 1000)
                .toISOString()
                .split("T")[0]
            : "",
        );
      } catch (e) {
        console.warn(
          "Invalid date of birth found in profile data:",
          profileToEdit.dob,
        );
        setDob(""); // Set to empty if date is invalid
      }
      setPrimaryCaregiver(profileToEdit.primaryCaregiver || "");
      setEmergencyContactName(profileToEdit.emergencyContactName || "");
      setEmergencyContactPhone(profileToEdit.emergencyContactPhone || "");
    } else if (isInitialProfile) {
      setProfileName(initialProfileName);
    }
  }, [profileToEdit, isInitialProfile, initialProfileName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!profileName.trim()) {
      setError("Profile name cannot be empty.");
      return;
    }
    if (dob && new Date(dob) > new Date()) {
      setError("Date of Birth cannot be in the future.");
      return;
    }
    setIsLoading(true);
    try {
      const profileData = {
        profileName: profileName.trim(),
        dob: dob ? Timestamp.fromDate(new Date(dob)) : null,
        primaryCaregiver: primaryCaregiver.trim(),
        emergencyContactName: emergencyContactName.trim(),
        emergencyContactPhone: emergencyContactPhone.trim(),
      };
      if (profileToEdit) {
        await updateCareProfile(profileToEdit.id, profileData);
      } else {
        await addCareProfile(profileData);
      }
      // Always call the callback on success
      if (onFormSubmit) {
        onFormSubmit(); // Callback to close modal, refresh list, or navigate
      }
    } catch (err) {
      console.error("Error saving profile:", err);
      setError(err.message || "Failed to save profile.");
    }
    setIsLoading(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 p-4 bg-white rounded-lg shadow-md"
    >
      <div>
        <label
          htmlFor="profileName"
          className="block text-sm font-medium text-gray-700"
        >
          Profile Name*
        </label>
        <input
          type="text"
          name="profileName"
          id="profileName"
          value={profileName}
          onChange={(e) => setProfileName(e.target.value)}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
          required
          placeholder="E.g., Mom, John D., Maya"
        />
      </div>

      {profileToEdit && (
        <>
          <div>
            <label
              htmlFor="dob"
              className="block text-sm font-medium text-gray-700"
            >
              Date of Birth
            </label>
            <input
              type="date"
              name="dob"
              id="dob"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
            />
          </div>

          <div>
            <label
              htmlFor="primaryCaregiver"
              className="block text-sm font-medium text-gray-700"
            >
              Primary Support Person
            </label>
            <input
              type="text"
              name="primaryCaregiver"
              id="primaryCaregiver"
              value={primaryCaregiver}
              onChange={(e) => setPrimaryCaregiver(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
              placeholder="E.g., Jane Doe"
            />
          </div>

          <div>
            <label
              htmlFor="emergencyContactName"
              className="block text-sm font-medium text-gray-700"
            >
              Emergency Contact Name
            </label>
            <input
              type="text"
              name="emergencyContactName"
              id="emergencyContactName"
              value={emergencyContactName}
              onChange={(e) => setEmergencyContactName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
              placeholder="E.g., Family Member"
            />
          </div>

          <div>
            <label
              htmlFor="emergencyContactPhone"
              className="block text-sm font-medium text-gray-700"
            >
              Emergency Contact Phone
            </label>
            <input
              type="tel"
              name="emergencyContactPhone"
              id="emergencyContactPhone"
              value={emergencyContactPhone}
              onChange={(e) => setEmergencyContactPhone(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
              placeholder="E.g., 555-123-4567"
            />
          </div>
        </>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end space-x-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
        >
          {isLoading
            ? profileToEdit
              ? "Updating..."
              : "Saving..."
            : profileToEdit
              ? "Update Profile"
              : "Create Profile"}
        </button>
      </div>
    </form>
  );
};

export default CareProfileForm;
